import { createSelector } from '@reduxjs/toolkit';
import { useContext } from 'preact/hooks';

import type { DialogButtonTemplateParams } from 'Components/smallComponents/dialogButton';
import type { RepeaterFunctionInvoker, TemplateFunction, TemplateResult } from 'Components/types';
import { garageCleanupRequested, vehicleDiscardingRequested } from 'Core/actions/fitmentSearch/index';
import { garageDataSelector } from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';

import type { GarageVehicleTemplateParams } from '../../types';
import VehicleWidget from '../../vehicleWidget/index';
import type { VehicleWidgetTemplateParams } from '../../vehicleWidget/vehicleWidget';
import AddButton from '../addButton';
import { vehicleWidgetName } from '../garage';
import GarageContext from '../garageContext';
import GarageItem from './garageItem';

interface Props {
  template: TemplateFunction<ListDropdownTemplateParams>;
}

export interface ListDropdownTemplateParams {
  addButton: (templ: TemplateFunction<DialogButtonTemplateParams>) => TemplateResult;
  closeGarage?: () => void;
  vehicleWidget: (templ: TemplateFunction<VehicleWidgetTemplateParams>) => TemplateResult;
  items: RepeaterFunctionInvoker<GarageVehicleTemplateParams>;
  isVehicleSelected?: boolean;
  vehicle?: Record<string, string>;
  discardSelectedVehicle: () => void;
  template: 'list';
}

export default function ListDropdown({ template }: Props): TemplateResult {
  const dispatch = useDispatch();

  const {
    closeGarage,
    doNotRedirectOnVehicleSelect,
    isAutoVehicleSelectionDisabled,
    redirectUrl,
    showVehicleDropdowns,
    useNativeDropdown,
    useVehicleWidget,
    isVehicleSelected,
    vehicle,
  } = useContext(GarageContext);

  const garageItems = useSelector(
    createSelector(garageDataSelector, (data) => data.repeaterComponents(GarageItem)),
  );

  const clearGarage = () => {
    closeGarage?.();
    dispatch(garageCleanupRequested(isVehicleSelected ?? false));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const discardSelectedVehicle = () => {
    dispatch(vehicleDiscardingRequested());
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const onClick = simpleHandler({ clearGarage, showVehicleDropdowns });

  const addButton = (templ: TemplateFunction<DialogButtonTemplateParams>) => (
    <AddButton template={templ} key="add" />
  );

  const vehicleWidget = (templ: TemplateFunction<VehicleWidgetTemplateParams>) => {
    if (!useVehicleWidget) {
      return null;
    }

    const props = {
      isAlwaysActive: true,
      isAlwaysColumnLayout: true,
      isAutoVehicleSelectionDisabled,
      name: vehicleWidgetName,
      onSelectVehicle: () => closeGarage?.(),
      doNotRedirectOnVehicleSelect,
      redirectUrl,
      useNativeDropdown,
      template: templ,
    };
    return <VehicleWidget {...props} key={vehicleWidgetName} />;
  };

  const component = template.call({
    addButton,
    closeGarage,
    vehicleWidget,
    items: garageItems,
    isVehicleSelected,
    vehicle,
    discardSelectedVehicle,
    template: 'list',
  });
  return cloneSafe(component, null, { onClick });
}
